/**
 * Hook to monitor session state and show expiration modal
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useNavigate } from 'react-router-dom';
import { sessionManager } from '../services/sessionManager';
import { tokenRefresher } from '../services/tokenRefresher';
import { createFileLogger } from '../utils/logger';

const log = createFileLogger('SESSION_MONITOR');

export const useSessionMonitor = () => {
  const [isSessionExpiredModalOpen, setIsSessionExpiredModalOpen] = useState(false);
  const [sessionContext, setSessionContext] = useState<any>(null);
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  // Handle session expired event
  useEffect(() => {
    const unsubscribe = sessionManager.onSessionExpired((context) => {
      log.warn('Session expired event received', context);
      setSessionContext(context);
      setIsSessionExpiredModalOpen(true);
    });

    return unsubscribe;
  }, []);

  // Monitor token expiration proactively
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkTokenExpiry = async () => {
      const authToken = localStorage.getItem('authToken');
      const idToken = localStorage.getItem('id_token');
      const token = authToken || idToken;

      if (!token) {
        log.warn('No token found during expiry check');
        return;
      }

      if (sessionManager.isTokenExpired(token)) {
        log.warn('Token expired — attempting silent refresh before signing out');
        const newToken = await tokenRefresher.tryRefresh();
        if (newToken) {
          log.log('Silent refresh succeeded — session preserved');
          // Schedule the next check against the new token's expiry
          const timeUntilExpiry = sessionManager.getTimeUntilExpiry(newToken);
          if (timeUntilExpiry) {
            const checkInterval = Math.min(timeUntilExpiry - 60000, 60000);
            if (checkInterval > 0) {
              setTimeout(checkTokenExpiry, checkInterval);
            }
          }
          return;
        }
        log.warn('Silent refresh failed — triggering session expired');
        sessionManager.handleSessionExpired();
      } else {
        // Schedule next check based on time until expiry
        const timeUntilExpiry = sessionManager.getTimeUntilExpiry(token);
        if (timeUntilExpiry) {
          const checkInterval = Math.min(timeUntilExpiry - 60000, 60000); // Check 1 min before expiry or every minute
          if (checkInterval > 0) {
            setTimeout(checkTokenExpiry, checkInterval);
          }
        }
      }
    };

    // Initial check
    checkTokenExpiry();

    // Also check periodically (fallback)
    const interval = setInterval(checkTokenExpiry, 5 * 60 * 1000); // Every 5 minutes

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Handle login action from modal
  const handleLogin = useCallback(() => {
    log.log('User clicked login from session expired modal');
    
    // Clear auth state but preserve redirect
    const redirectPath = localStorage.getItem('redirectAfterLogin');
    logout();
    
    // Close modal
    setIsSessionExpiredModalOpen(false);
    sessionManager.reset();
    
    // Navigate to home (which will show login)
    navigate('/', { replace: true });
    
    // Restore redirect path if it was cleared
    if (redirectPath) {
      localStorage.setItem('redirectAfterLogin', redirectPath);
    }
  }, [logout, navigate]);

  return {
    isSessionExpiredModalOpen,
    sessionContext,
    handleLogin
  };
};


